import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  CircularProgress,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel, Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import api from '../api/api';

const AssetReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState({
    total_assets: 0,
    total_revenue: 0,
    total_rentals: 0,
    avg_revenue_per_asset: 0,
    total_investment: 0,
    overall_profitability_percentage: 0
  });
  
  // Фильтры
  const [searchQuery, setSearchQuery] = useState('');
  const [rentalPointId, setRentalPointId] = useState('');
  const [category, setCategory] = useState('');
  const [minRevenue, setMinRevenue] = useState('');
  const [maxRevenue, setMaxRevenue] = useState('');
  const [minRentals, setMinRentals] = useState('');
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Пагинация
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  
  // Списки для фильтров
  const [rentalPoints, setRentalPoints] = useState([]);

  useEffect(() => {
    fetchRentalPoints();
    fetchReports();
    fetchSummary();
  }, [page, pageSize, sortBy, sortOrder, refreshVersion]);

  const fetchRentalPoints = async () => {
    try {
      const response = await api.get('/rental-points/');
      setRentalPoints(response.data);
    } catch (error) {
      console.error('Ошибка загрузки точек аренды:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (rentalPointId) params.append('rental_point_id', rentalPointId);
      if (category) params.append('category', category);
      if (minRevenue) params.append('min_revenue', minRevenue);
      if (maxRevenue) params.append('max_revenue', maxRevenue);
      if (minRentals) params.append('min_rentals', minRentals);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('page', page);
      params.append('limit', pageSize);

      const response = await api.get(`/reports/assets?${params.toString()}`);
      setAssets(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Ошибка загрузки отчета:', error);
      setLoadError('Не удалось загрузить отчёт. Повторите запрос.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/reports/assets/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Ошибка загрузки сводки:', error);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    setRefreshVersion(version => version + 1);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setRentalPointId('');
    setCategory('');
    setMinRevenue('');
    setMaxRevenue('');
    setMinRentals('');
    setPage(1);
    setRefreshVersion(version => version + 1);
  };

  // Обработка сортировки по клику на заголовок колонки
  const handleSortRequest = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
    setPage(1);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'rented':
        return 'warning';
      case 'maintenance':
        return 'info';
      case 'unavailable':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available':
        return 'Доступен';
      case 'rented':
        return 'В аренде';
      case 'maintenance':
        return 'На обслуживании';
      case 'unavailable':
        return 'Недоступен';
      default:
        return status;
    }
  };

  const getProfitabilityColor = (percentage) => {
    if (percentage >= 100) return 'success';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  return (
    <Box className="erp-report-page" sx={{ p: 3 }}>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        📊 Отчеты по активам
      </Typography>

      {/* Статистические карточки */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Всего активов
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {summary.total_assets}
                  </Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 50, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #a5652c 0%, #ba834e 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Общая выручка
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {formatCurrency(summary.total_revenue)}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 50, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Аренд
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {summary.total_rentals}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 50, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Окупаемость
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {summary.overall_profitability_percentage.toFixed(1)}%
                  </Typography>
                </Box>
                <ShoppingCartIcon sx={{ fontSize: 50, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Фильтры */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Фильтры
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Поиск по названию"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="asset-report-point-label">Точка аренды</InputLabel>
              <Select
                labelId="asset-report-point-label"
                value={rentalPointId}
                onChange={(e) => setRentalPointId(e.target.value)}
                label="Точка аренды"
              >
                <MenuItem value="">Все</MenuItem>
                {rentalPoints.map((point) => (
                  <MenuItem key={point.id} value={point.id}>
                    {point.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Категория"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Мин. выручка"
              value={minRevenue}
              onChange={(e) => setMinRevenue(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Макс. выручка"
              value={maxRevenue}
              onChange={(e) => setMaxRevenue(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Мин. аренд"
              value={minRentals}
              onChange={(e) => setMinRentals(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="asset-report-sort-label">Сортировка</InputLabel>
              <Select
                labelId="asset-report-sort-label"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Сортировка"
              >
                <MenuItem value="revenue">По выручке</MenuItem>
                <MenuItem value="rental_count">По аренд</MenuItem>
                <MenuItem value="profitability">По окупаемости</MenuItem>
                <MenuItem value="name">По названию</MenuItem>
                <MenuItem value="asset_number">По номеру</MenuItem>
                <MenuItem value="rental_point">По точке аренды</MenuItem>
                <MenuItem value="category">По категории</MenuItem>
                <MenuItem value="purchase_price">По стоимости</MenuItem>
                <MenuItem value="status">По статусу</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="asset-report-order-label">Порядок</InputLabel>
              <Select
                labelId="asset-report-order-label"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                label="Порядок"
              >
                <MenuItem value="desc">По убыванию</MenuItem>
                <MenuItem value="asc">По возрастанию</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyFilters}
          >
            Применить
          </Button>
          <Button
            variant="outlined"
            onClick={handleResetFilters}
          >
            Сбросить
          </Button>
        </Box>
      </Paper>

      {/* Таблица активов */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              📋 Список активов
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Всего: {total} активов
            </Typography>
          </Box>
          
          {/* Выбор количества записей */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Показывать:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(e.target.value);
                  setPage(1);
                }}
                sx={{ height: 32 }}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <strong>№</strong>
                      <IconButton onClick={() => handleSortRequest('asset_number')} size="small">
                        {sortBy === 'asset_number' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <strong>Название</strong>
                      <IconButton onClick={() => handleSortRequest('name')} size="small">
                        {sortBy === 'name' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <strong>Точка аренды</strong>
                      <IconButton onClick={() => handleSortRequest('rental_point')} size="small">
                        {sortBy === 'rental_point' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <strong>Категория</strong>
                      <IconButton onClick={() => handleSortRequest('category')} size="small">
                        {sortBy === 'category' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <strong>Аренд</strong>
                      <IconButton onClick={() => handleSortRequest('rental_count')} size="small">
                        {sortBy === 'rental_count' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <strong>Выручка</strong>
                      <IconButton onClick={() => handleSortRequest('revenue')} size="small">
                        {sortBy === 'revenue' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <strong>Стоимость</strong>
                      <IconButton onClick={() => handleSortRequest('purchase_price')} size="small">
                        {sortBy === 'purchase_price' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <strong>Окупаемость</strong>
                      <IconButton onClick={() => handleSortRequest('profitability')} size="small">
                        {sortBy === 'profitability' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <strong>Статус</strong>
                      <IconButton onClick={() => handleSortRequest('status')} size="small">
                        {sortBy === 'status' && sortOrder === 'asc' ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow 
                    key={asset.id} 
                    hover
                    onClick={() => navigate(`/assets/edit/${asset.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{asset.asset_number || '-'}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 500,
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {asset.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{asset.rental_point}</TableCell>
                    <TableCell>{asset.category}</TableCell>
                    <TableCell align="center">{asset.rental_count}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: 'success.main' }}
                      >
                        {formatCurrency(asset.total_revenue)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(asset.purchase_price)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${asset.profitability_percentage}%`}
                        color={getProfitabilityColor(asset.profitability_percentage)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getStatusLabel(asset.status)}
                        color={getStatusColor(asset.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Пагинация */}
        {!loading && total > pageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
            <Button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              variant="outlined"
            >
              Назад
            </Button>
            <Typography sx={{ alignSelf: 'center', mx: 2 }}>
              Страница {page} из {Math.ceil(total / pageSize)}
            </Typography>
            <Button
              disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage(page + 1)}
              variant="outlined"
            >
              Вперед
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AssetReportsPage;
